/** Initial audio pipeline counter state. */
export function createAudioDiagnostics() {
  return {
    subscriptions: 0,
    configPackets: 0,
    framePackets: 0,
    decodedFrames: 0,
    playedFrames: 0,
    droppedFrames: 0,
    pendingEnqueued: 0,
    underruns: 0,
    encodeBackpressure: 0,
    reconnects: 0,
    mixerMode: "—",
    contextState: "—",
    bufferedMs: 0,
    targetMs: 50,
    catchUpMs: 0,
    lastEvent: "idle",
    lastSignificantEvent: "idle",
    lastChannel: "",
    lastFrameAt: 0,
    lastPlayedAt: 0,
    startedAt: Date.now(),
    dropsByType: {},
    recentEvents: []
  };
}

const DROP_EVENT_TYPES = new Set([
  "audio_dropped_no_decoder",
  "audio_dropped_pending_full",
  "audio_dropped_decode_exception",
  "audio_dropped_context",
  "audio_dropped_start",
  "audio_decoder_error",
  "audio_subscription_error"
]);

const RECENT_LIMIT = 14;

/** Events that fire dozens of times per second — hidden from UI flicker. */
const NOISY_EVENT_TYPES = new Set([
  "audio_played",
  "audio_decoded",
  "audio_frame_packet",
  "audio_config_packet",
  "audio_buffer_stats"
]);

/** Important events for the "last" line and feed. */
export function isSignificantDiagnosticEvent(type) {
  return !NOISY_EVENT_TYPES.has(type);
}

/**
 * Updates diagnostics state from a transport / decode / mixer event.
 * @param {ReturnType<typeof createAudioDiagnostics>} state
 * @param {{ type: string, channel?: string, mode?: string, state?: string, count?: number, bufferedMs?: number, targetMs?: number, catchUpMs?: number }} event
 */
export function recordAudioDiagnostic(state, event) {
  const next = { ...state };

  if (isSignificantDiagnosticEvent(event.type)) {
    next.lastEvent = event.type;
    next.lastSignificantEvent = event.type;
    if (event.channel) next.lastChannel = event.channel;
  }

  if (event.type === "audio_subscribed") next.subscriptions += 1;
  if (event.type === "audio_unsubscribed") next.subscriptions = Math.max(0, next.subscriptions - 1);
  if (event.type === "audio_config_packet") next.configPackets += 1;
  if (event.type === "audio_frame_packet") {
    next.framePackets += 1;
    next.lastFrameAt = Date.now();
  }
  if (event.type === "audio_decoded") next.decodedFrames += 1;
  if (event.type === "audio_played") {
    next.playedFrames += 1;
    next.lastPlayedAt = Date.now();
  }
  if (event.type === "audio_pending_decode") next.pendingEnqueued += 1;
  if (event.type === "audio_encode_backpressure") next.encodeBackpressure += 1;
  if (event.type === "audio_underrun") next.underruns = event.count ?? next.underruns + 1;
  if (event.type === "audio_buffer_stats") {
    if (Number.isFinite(event.bufferedMs)) next.bufferedMs = event.bufferedMs;
    if (Number.isFinite(event.targetMs)) next.targetMs = event.targetMs;
    if (Number.isFinite(event.catchUpMs)) next.catchUpMs = event.catchUpMs;
    if (Number.isFinite(event.count)) next.underruns = event.count;
  }
  if (event.type === "audio_subscription_reconnecting") next.reconnects += 1;
  if (event.type === "audio_mixer_running") {
    next.mixerMode = event.mode || next.mixerMode;
    next.contextState = event.state || next.contextState;
  }
  if (event.type === "audio_context_resumed") {
    next.contextState = event.state || "running";
  }

  if (DROP_EVENT_TYPES.has(event.type) || event.type.startsWith("audio_dropped")) {
    next.droppedFrames += 1;
    next.dropsByType = { ...next.dropsByType, [event.type]: (next.dropsByType[event.type] || 0) + 1 };
  }

  if (
    event.type !== "audio_played"
    && event.type !== "audio_decoded"
    && event.type !== "audio_frame_packet"
    && event.type !== "audio_config_packet"
    && event.type !== "audio_buffer_stats"
  ) {
    const recent = [{ type: event.type, channel: event.channel || "", at: Date.now() }, ...next.recentEvents];
    next.recentEvents = recent.slice(0, RECENT_LIMIT);
  }

  return next;
}

/** Share of successful stages, 0–100. */
function pct(part, total) {
  if (!total) return 100;
  return Math.round((part / total) * 100);
}

/** Seconds since session start. */
function sessionSec(state) {
  return Math.max(1, Math.round((Date.now() - state.startedAt) / 1000));
}

/**
 * Short line for the control panel.
 * @param {ReturnType<typeof createAudioDiagnostics>} state
 */
export function formatAudioSummary(state) {
  const health = state.framePackets ? pct(state.decodedFrames, state.framePackets) : 100;
  const playFps = state.playedFrames ? Math.round(state.playedFrames / sessionSec(state)) : 0;
  const recvFps = state.framePackets ? Math.round(state.framePackets / sessionSec(state)) : 0;
  const note = eventLabel(state.lastSignificantEvent);
  return `audio: ${health}% · buf ${state.bufferedMs}/${state.targetMs} ms · catch ${state.catchUpMs} ms · ~${playFps} fps · und ${state.underruns} · ${note}`;
}

/**
 * Hints based on current metrics.
 * @param {ReturnType<typeof createAudioDiagnostics>} state
 * @param {{ remoteMicOn?: boolean, remoteSpeaking?: boolean }} [presence]
 */
export function audioDiagnosticHints(state, presence = {}) {
  const hints = [];
  const sec = sessionSec(state);
  const decodePct = pct(state.decodedFrames, state.framePackets);
  const playPct = pct(state.playedFrames, state.decodedFrames);
  const remoteMicOn = presence.remoteMicOn;

  if (state.framePackets === 0) {
    if (remoteMicOn === false) {
      hints.push("No packets yet — remote microphone is off.");
      return hints;
    }
    hints.push("No packets yet — waiting for media.audio.opus subscription.");
    return hints;
  }

  if (state.contextState !== "running") {
    hints.push("AudioContext is not running — click the page to unlock audio.");
  }

  const idleSec = state.lastFrameAt ? Math.round((Date.now() - state.lastFrameAt) / 1000) : 0;
  if (state.playedFrames > 0 && idleSec > 3) {
    if (remoteMicOn === false) {
      hints.push(`No Opus for ${idleSec} s — remote microphone is off.`);
    } else {
      hints.push(`No packets for ${idleSec} s — check audio_subscription_ended / network.`);
    }
  }

  if (state.reconnects > 0) {
    hints.push(`Audio subscription reconnected ${state.reconnects}× (auto after recv disconnect).`);
  }

  const recentEnded = state.recentEvents.some(item => item.type === "audio_subscription_ended");
  if (recentEnded) {
    hints.push("recv() recently returned null — subscription restarts; audio may drop for ~1 s.");
  }

  if (decodePct < 92) {
    hints.push(`Only ${decodePct}% of packets decoded — see "losses by type" (decoder queue / network).`);
  } else if (decodePct < 99) {
    hints.push(`Rare pre-decode losses (${decodePct}%) — usually a brief load spike.`);
  }

  if (state.playedFrames > 0 && playPct < 99) {
    hints.push("Some PCM did not reach the mixer — check audio_dropped_context / audio_dropped_start.");
  }

  if (state.underruns > 0) {
    const undPerMin = Math.round((state.underruns / sec) * 60);
    if (undPerMin > 30) {
      hints.push(`Worklet underrun ~${undPerMin}/min — target may rise to 100/150 ms.`);
    } else {
      hints.push(`Rare underruns (${state.underruns} in ${sec} s) — usually inaudible.`);
    }
  }

  if (state.targetMs > 50) {
    hints.push(`Jitter buffer target ${state.targetMs} ms (raised after underruns; steps down when stable).`);
  }

  if (state.catchUpMs > 0) {
    hints.push(`Pause catch-up compressed ${state.catchUpMs} ms of noise (overlap-add), speech untouched.`);
  }

  if (state.encodeBackpressure > 5) {
    hints.push("Microphone: encoder often waited on queue — CPU or slow FastPubSub publish.");
  }

  if (state.pendingEnqueued > state.droppedFrames && state.pendingEnqueued > 20) {
    hints.push("Decoder queued packets in pending — brief overload, not a full drop.");
  }

  if ((state.dropsByType.audio_subscription_error || 0) > 0) {
    hints.push("Audio subscription error — WebSocket / FastPubSub may have disconnected.");
  }

  if ((state.dropsByType.audio_dropped_no_decoder || 0) > 0) {
    hints.push(`Lost ${state.dropsByType.audio_dropped_no_decoder} frames before Opus config (usually only on connect).`);
  }

  if ((state.dropsByType.audio_dropped_pending_full || 0) > 0) {
    hints.push("Pending queue overflow — decoder is far behind.");
  }

  if (hints.length === 0 && state.playedFrames > 0) {
    hints.push(`Audio is healthy (~${Math.round(state.playedFrames / sec)} play fps over ${sec} s).`);
  }

  return hints;
}

/**
 * Full text for the expanded panel.
 * @param {ReturnType<typeof createAudioDiagnostics>} state
 * @param {{ remoteMicOn?: boolean, remoteSpeaking?: boolean }} [presence]
 */
export function formatAudioDetail(state, presence = {}) {
  const sec = sessionSec(state);
  const lines = [
    "=== Audio: receive (listener) ===",
    `session: ${sec} s · subscriptions: ${state.subscriptions} · config: ${state.configPackets}`,
    `recv (frames): ${state.framePackets} · decoded: ${state.decodedFrames} · played: ${state.playedFrames}`,
    `decode: ${pct(state.decodedFrames, state.framePackets)}% · play/decode: ${pct(state.playedFrames, state.decodedFrames)}%`,
    `drop total: ${state.droppedFrames} · pending queued: ${state.pendingEnqueued} · underrun: ${state.underruns} · reconnect: ${state.reconnects}`,
  ];

  lines.push("", "=== Playback / jitter buffer ===");
  lines.push(`mixer: ${state.mixerMode} · AudioContext: ${state.contextState}`);
  lines.push(`buffer: ${state.bufferedMs} ms · target: ${state.targetMs} ms · catch-up: ${state.catchUpMs} ms`);
  lines.push(`activity: ~${Math.round(state.playedFrames / sec)} play fps · ~${Math.round(state.framePackets / sec)} recv fps`);
  lines.push(`last significant: ${eventLabel(state.lastSignificantEvent)}${state.lastChannel ? ` · ${shortChannel(state.lastChannel)}` : ""}`);

  const dropTypes = Object.entries(state.dropsByType);
  lines.push("", "=== Losses by type ===");
  if (dropTypes.length === 0) {
    lines.push("none");
  } else {
    for (const [type, count] of dropTypes.sort((a, b) => b[1] - a[1])) {
      lines.push(`${type}: ${count} — ${dropTypeHint(type)}`);
    }
  }

  lines.push("", "=== Microphone (sender) ===");
  lines.push(`mode: continuous Opus · encode backpressure: ${state.encodeBackpressure}`);

  lines.push("", "=== Hints ===");
  for (const hint of audioDiagnosticHints(state, presence)) {
    lines.push(`• ${hint}`);
  }

  if (state.recentEvents.length > 0) {
    lines.push("", "=== Recent events ===");
    for (const item of state.recentEvents) {
      const age = Math.round((Date.now() - item.at) / 1000);
      lines.push(`${age}s ago: ${eventLabel(item.type)}${item.channel ? ` · ${shortChannel(item.channel)}` : ""}`);
    }
  }

  return lines.join("\n");
}

/** Short channel name for logs. */
function shortChannel(channel) {
  const parts = channel.split(".");
  if (parts.length >= 3) return parts.slice(-3).join(".");
  return channel;
}

/** Human-readable event name. */
function eventLabel(type) {
  const labels = {
    idle: "waiting",
    audio_subscribed: "subscription ok",
    audio_unsubscribed: "unsubscribed",
    audio_mixer_running: "mixer running",
    audio_decoder_configured: "decoder ready",
    audio_underrun: "underrun worklet",
    audio_buffer_stats: "buffer stats",
    audio_pending_decode: "decode queue",
    audio_encode_backpressure: "encoder waited",
    audio_context_resumed: "AudioContext resume",
    audio_subscription_ended: "subscription ended",
    audio_subscription_reconnecting: "resubscribing",
    audio_subscription_error: "subscription error",
    audio_early_buffered: "buffer before config",
    audio_dropped_no_decoder: "drop: no decoder",
    audio_dropped_pending_full: "drop: pending full",
    audio_dropped_decode_exception: "drop: decode",
    audio_dropped_context: "drop: context",
    audio_dropped_start: "drop: start",
    audio_decoder_error: "decoder error"
  };
  return labels[type] || type;
}

/** Drop type explanation for the UI. */
function dropTypeHint(type) {
  const hints = {
    audio_dropped_no_decoder: "frame arrived before Opus config",
    audio_dropped_pending_full: "decoder wait queue overflow",
    audio_dropped_decode_exception: "decode() error",
    audio_dropped_context: "AudioContext not running",
    audio_dropped_start: "source.start() failed",
    audio_decoder_error: "AudioDecoder failure",
    audio_subscription_error: "FastPubSub subscription ended"
  };
  return hints[type] || "see console";
}
